import React, { useCallback, useEffect, useState } from "react";
import { BoltIcon, UsersIcon, DevicePhoneMobileIcon, TagIcon } from "@heroicons/react/24/outline";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getAdminStats } from "../api/monitoringAPI";
import { Card, Kpi, KpiSkeleton, ErrorState, Empty, RefreshBtn, Segmented, RankList, useChartTheme, periodRange, PERIODS, fmt } from "./monitoring/ui";

const rateText = (value, type) => {
  if (value && typeof value === "object") {
    if (typeof value.success_rate === "number") return `${value.success_rate}%`;
    if (typeof value.success === "number" && typeof value.total === "number" && value.total) {
      const rate = type === "failure" ? ((value.failure || 0) / value.total) * 100 : (value.success / value.total) * 100;
      return `${rate.toFixed(1)}%`;
    }
    return "—";
  }
  return value === null || value === undefined ? "—" : `${value}%`;
};

const AdminStats = () => {
  const [period, setPeriod] = useState(30);
  const [stats, setStats] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const chart = useChartTheme();

  const fetchStats = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      setStats(await getAdminStats(periodRange(period)));
      setState("ready");
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError(err.message);
      setState("error");
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const daily = (stats?.daily_statistics || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" }),
    events: d.events_count || 0,
    users: d.unique_users || 0,
  }));
  const eventTypes = [...(stats?.event_type_breakdown || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
  const rateKeys = [...new Set([...Object.keys(stats?.success_rates || {}), ...Object.keys(stats?.failure_rates || {})])];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">События мобильного приложения и сайта, собранные нашим backend.</p>
        <div className="flex items-center gap-2">
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
          <RefreshBtn onClick={fetchStats} loading={state === "loading"} />
        </div>
      </div>

      {state === "error" ? (
        <ErrorState text="Не удалось загрузить статистику" detail={error} onRetry={fetchStats} />
      ) : !stats ? (
        <>
          <KpiSkeleton />
          <div className="h-80 animate-pulse rounded-block border border-border-primary bg-bg-card" />
        </>
      ) : (
        <div className={`space-y-4 ${state === "loading" ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Событий" value={stats.total_events || 0} icon={BoltIcon} />
            <Kpi label="Уникальных пользователей" value={stats.unique_users || 0} icon={UsersIcon} tone="bg-emerald-500/15 text-emerald-500" />
            <Kpi label="Уникальных устройств" value={stats.unique_devices || 0} icon={DevicePhoneMobileIcon} tone="bg-sky-500/15 text-sky-500" />
            <Kpi label="Типов событий" value={eventTypes.length} icon={TagIcon} tone="bg-amber-500/15 text-amber-500" />
          </div>

          <Card title="Активность по дням" subtitle="События и уникальные пользователи">
            {daily.length ? (
              <div className="h-72 px-2 py-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gEvents" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#14B8A6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                    <XAxis dataKey="date" tick={chart.axis} axisLine={false} tickLine={false} minTickGap={12} />
                    <YAxis tick={chart.axis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...chart.tooltip} formatter={(v, n) => [fmt(v), n]} />
                    <Area type="monotone" dataKey="events" name="События" stroke="#6366F1" strokeWidth={2} fill="url(#gEvents)" />
                    <Area type="monotone" dataKey="users" name="Пользователи" stroke="#14B8A6" strokeWidth={2} fill="url(#gUsers)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty>Нет событий за период</Empty>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Типы событий" subtitle={`${eventTypes.length} типов`}>
              <div className="max-h-96 overflow-y-auto">
                <RankList items={eventTypes.map((e) => ({ label: e.event_type, value: e.count || 0 }))} emptyText="Нет событий" />
              </div>
            </Card>

            <Card title="Успешность операций" subtitle="Доля успешных и неудачных по типам">
              {rateKeys.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-text-muted">
                      <th className="px-5 py-2 text-left font-medium">Операция</th>
                      <th className="px-5 py-2 text-right font-medium">Успех</th>
                      <th className="px-5 py-2 text-right font-medium">Ошибки</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {rateKeys.map((k) => (
                      <tr key={k}>
                        <td className="px-5 py-2 text-text-primary">{k}</td>
                        <td className="px-5 py-2 text-right font-semibold tabular-nums text-emerald-500">{stats.success_rates?.[k] !== undefined ? rateText(stats.success_rates[k], "success") : "—"}</td>
                        <td className="px-5 py-2 text-right font-semibold tabular-nums text-rose-500">{stats.failure_rates?.[k] !== undefined ? rateText(stats.failure_rates[k], "failure") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Empty>Нет данных об успешности</Empty>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Популярные экраны и страницы">
              <RankList items={(stats.most_visited_pages || []).slice(0, 10).map((p) => ({ label: p.page || p.path || "—", value: p.count || 0 }))} emptyText="Нет данных о посещениях" color="bg-sky-500" />
            </Card>
            <Card title="Популярные поисковые запросы">
              <RankList items={(stats.most_searched_queries || []).slice(0, 10).map((q) => ({ label: q.query || q.search_term || "—", value: q.count || 0 }))} emptyText="Поисковых запросов нет" color="bg-emerald-500" />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStats;
